import React from 'react';
import { ShopOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Image, Typography, Tag } from 'antd';

import TrackerService from '../../utils/TrackerService';
import { useAdForBrand } from '../../models/useAdForBrand';

const { Text } = Typography;

const BuyTippingStandsSection = ({ 
  clientId,
  client,
  screenWidth,
  isAdmin,
  brand,
  teamId,
  team,
}) => {
  // Salon-related category codes: hair (Hairdresser/Stylist), nails (Nail technician), 
  // beauty (Therapist), barber (Barber)
  const salonCategoryCodes = ['hair', 'nails', 'beauty', 'barber'];
  const isSalonCategory = client?.categoryCode && salonCategoryCodes.includes(client.categoryCode);
  
  // Fetch ad data for the specified brand
  const [ad, loading, error] = useAdForBrand(brand);
  
  // Only show for salon categories
  if (!isSalonCategory) {
    return null;
  }
  
  // Don't render if still loading or if no ad found
  if (loading || !ad) {
    return null;
  }
  
  const isMobile = screenWidth <= 768;
  
  // Use ad data or fallback to defaults
  const title = ad.displayName || 'Get your tipping stand to start accepting tips';
  const description = ad.description || 'Order your preferred devices from our online store and get them shipped directly to you.';
  const linkUrl = ad.link || 'https://www.amazon.co.uk/stores/page/A8F80454-DE91-4C1D-9EE7-F25AA758B466?ingress=3';
  const imageUrl = ad.imageUrl || '/images/tipping-stand-thumbnail.png';
  const buttonColor = ad.rgbColor || '#000000';
  
  const handleBuyStandsClick = () => {
    // Mixpanel tracking - ad_clicked
    const isLocalHost = window.location.hostname === 'localhost';
    const isTest = isLocalHost;
    const tracker = new TrackerService({ isTest });
    
    const data = {
      // Client info
      clientId,
      clientDisplayName: client?.displayName || null,
      clientEmail: client?.email || null,
      // Ad info
      brand,
      url: linkUrl,
      displayName: ad.displayName || null,
      description: ad.description || null,
      is_test: isTest,
    };

    // Add team info if provided
    if (teamId && team) {
      data.teamId = teamId;
      data.teamName = team.teamName || null;
      data.teamOwnerClientId = team.teamOwnerClientId || null;
      data.teamOwnerDisplayName = team.teamOwnerDisplayName || null;
      data.teamEmail = team.teamOwnerEmail || null;
      data.currency = team.defaultCurrency || null;
    }

    tracker.mixpanelTrack('ad_clicked', data);

    if (!isTest) {
      tracker.googleTrack('ad_clicked', data);
    }

    window.open(linkUrl, '_blank');
  };

  return (
    <>
      <Card
        style={{
          width: '100%',
          borderRadius: '8px',
          border: '1px solid #d9d9d9',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: '24px',
          position: 'relative'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        {isAdmin && (
          <Tag
            color="default"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 1,
              backgroundColor: '#000000',
              color: '#ffffff',
              borderColor: '#000000'
            }}
          >
            {brand}
          </Tag>
        )}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}>
          <div style={{ flex: 1, minWidth: isMobile ? '0' : '200px', width: isMobile ? '100%' : 'auto' }}>
            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '12px' }}>
              {title}
            </Text>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'flex-start',
              gap: '16px'
            }}>
              <Image
                src={imageUrl}
                alt={title}
                style={{ 
                  width: isMobile ? '100%' : '150px',
                  maxWidth: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
                preview={false}
              />
              <Text style={{ fontSize: '14px', display: 'block', color: '#595959', flex: 1 }}>
                {description}
              </Text>
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: isMobile ? 'stretch' : 'flex-end',
            gap: '12px', 
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto'
          }}>
            <Button
              type="default"
              size="large"
              icon={<ShopOutlined />}
              block={isMobile}
              onClick={handleBuyStandsClick}
              style={{
                height: '40px',
                padding: '0 24px',
                fontSize: '16px',
                fontWeight: 500,
                borderRadius: '6px',
                backgroundColor: buttonColor,
                borderColor: buttonColor,
                color: '#ffffff',
                width: isMobile ? '100%' : '100%',
                minWidth: isMobile ? '0' : '180px'
              }}
            >
              Buy Tipping Stands
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default BuyTippingStandsSection;

